// Add to playlist from a home card (Oct 2026, user: "being able to add a tune
// to a playlist from the card is useful"): the first of the two buttons in a
// tile's corner, beside Love. It opens a small dialog with the
// same panel the header's and the search's Add to use. A dialog, not a
// popover: a shelf that scrolls sideways would clip a popover's panel.
//
// A card that knows the version it opens (Recent tunes, Most played, a Loved
// tune you opened) adds that version. A library card (Top picks, More like, a
// heart given to one) adds the version its link opens, found when the dialog
// opens (data/tuneLink.ts). The add says so at the foot of the screen: the app's
// one notice for that, with Open and Undo (playlistAdded.ts).
//
// The dialog is portaled out of the tile: a tile's buttons sit in a stacking
// context of their own, under the app bar. It goes into the app shell rather
// than the document body, where the visual style's colours and fonts apply.

import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { ListPlus } from 'lucide-react';
import { recordFeature } from '@/analytics/usage';
import { usePlaylistsAvailable } from '@/data/playlistAccess';
import { resolveTuneLinkRef } from '@/data/tuneLink';
import type { TasteTune } from '@/state/tuneTaste';
import { playlistRefKey, type PlaylistTuneRef } from '@/storage/playlists';
import { AddTuneToPlaylistPanel } from './AddTuneToPlaylist';
import { Modal } from './Modal';

type Found =
  | { status: 'ready'; ref: PlaylistTuneRef }
  | { status: 'loading' }
  | { status: 'missing' }
  | { status: 'failed' };

/** The version to add: the card's own, or the one its link opens. */
function useTileRef(tune: TasteTune): { found: Found; retry: () => void } {
  const [found, setFound] = useState<Found>(() =>
    tune.ref ? { status: 'ready', ref: tune.ref } : { status: 'loading' },
  );
  const [attempt, setAttempt] = useState(0);
  const { ref, title, composer, source } = tune;
  useEffect(() => {
    if (ref) {
      setFound({ status: 'ready', ref });
      return;
    }
    let live = true;
    resolveTuneLinkRef({ title, ...(composer ? { composer } : {}), ...(source ? { source } : {}) })
      .then((resolved) => {
        if (live) setFound(resolved ? { status: 'ready', ref: resolved } : { status: 'missing' });
      })
      .catch(() => {
        if (live) setFound({ status: 'failed' });
      });
    return () => {
      live = false;
    };
  }, [ref, title, composer, source, attempt]);
  const retry = () => {
    setFound({ status: 'loading' });
    setAttempt((value) => value + 1);
  };
  return { found, retry };
}

function TilePlaylistDialog({
  tune,
  host,
  onClose,
}: {
  tune: TasteTune;
  host: Element;
  onClose: () => void;
}) {
  const { found, retry } = useTileRef(tune);

  const added = () => recordFeature('card-add-to-playlist');

  return createPortal(
    <Modal title="Add to playlist" onClose={onClose} className="tile-playlist-dialog">
      <p className="tile-playlist-tune" data-testid="tile-playlist-tune">
        {tune.title}
        {tune.composer && <span className="muted"> · {tune.composer}</span>}
      </p>
      {found.status === 'ready' ? (
        <AddTuneToPlaylistPanel
          key={playlistRefKey(found.ref)}
          close={onClose}
          onAdded={added}
          tuneRef={found.ref}
        />
      ) : found.status === 'loading' ? (
        <p className="muted" role="status">
          Loading…
        </p>
      ) : found.status === 'missing' ? (
        <p className="import-error" role="alert">
          Couldn&apos;t find this tune.
        </p>
      ) : (
        <p className="import-error" role="alert">
          Couldn&apos;t load this tune.{' '}
          <button type="button" className="btn btn-sm" onClick={retry}>
            Try again
          </button>
        </p>
      )}
    </Modal>,
    host,
  );
}

/** The button in a tile's corner; nothing while playlists are unavailable. */
export function TileAddToPlaylist({ tune }: { tune: TasteTune }) {
  const available = usePlaylistsAvailable();
  const button = useRef<HTMLButtonElement>(null);
  const [host, setHost] = useState<Element | null>(null);
  if (!available) return null;
  return (
    <>
      <button
        ref={button}
        type="button"
        className="tile-action"
        aria-label={`Add ${tune.title} to a playlist`}
        aria-haspopup="dialog"
        title="Add to playlist"
        data-testid="tile-add-to-playlist"
        onClick={() => setHost(button.current?.closest('.app-shell') ?? document.body)}
      >
        <ListPlus size={17} aria-hidden="true" />
      </button>
      {host && <TilePlaylistDialog tune={tune} host={host} onClose={() => setHost(null)} />}
    </>
  );
}
